// Lịch sử chạy: lọc bot/trạng thái/từ-đến, phân trang "Tải thêm", Xuất Excel (CSV) (link /api/runs.csv cùng bộ lọc).
// N11 T13 (docs/phuong-an-gom-loi-chay.md): ?bundleName=<tên bundle> lọc theo MỘT kịch bản (liên kết "Lịch sử chạy của kịch bản này" ở thẻ
// nhiều bot) — chip "Đang lọc: <tên kịch bản> ×" (× bỏ lọc); bảng, "Tải thêm" và CSV cùng lọc; nút Lọc giữ lọc kịch bản trên địa chỉ.
import { api, buildQuery, errMsg } from "../api";
import { busy, clear, gen, h } from "../dom";
import { fmtTime, modeTagClass, modeText, modeTitle, NHOM_LOC_RUN, RUN_STATUSES, runCoCanhBao, runNhanHien, runStatusText, runStatusTitle } from "../format";
import { loadBots, store } from "../store";
import type { Paged, RunSummary } from "../types";
import { cuoiNgayVN, dauNgayVN, kiemKhoangNgay } from "../../../../src/shared/khoang-ngay";
import { ganLyDo, tenKb } from "../ten-kb"; // L-78 / N9: TÊN kịch bản; mã bước trong lý do → tên bước

export async function renderHistory(root: HTMLElement, _p: Record<string, string>, query: URLSearchParams): Promise<() => void> {
  const alive = gen("history");
  try { await loadBots(); } catch { /* vẫn hiện bảng, ô lọc bot trống */ }
  if (!alive()) return () => {};

  const botSel = h("select", { title: "Chỉ hiện lần chạy của bot này", "data-testid": "f-bot" }, h("option", { value: "" }, "Mọi bot"),
    ...[...store.bots.values()].sort((a, b) => a.name.localeCompare(b.name, "vi")).map((b) => h("option", { value: b.id }, b.name)));
  // L11: mặc định 5 nhóm dễ hiểu; tick "Lọc chi tiết" mới hiện từng trạng thái kỹ thuật (giá trị cũ ?status=<mã> trên địa chỉ vẫn mở đúng).
  const statusSel = h("select", { id: "f-status-sel", title: "Chỉ hiện lần chạy thuộc nhóm trạng thái này", "data-testid": "f-status" });
  const chiTiet = h("input", { type: "checkbox", title: "Hiện từng trạng thái riêng (mất liên lạc, hết giờ, chờ giãn cách…) thay vì 5 nhóm", "data-testid": "f-status-chi-tiet" });
  function veTrangThai(): void {
    const cu = statusSel.value;
    clear(statusSel);
    statusSel.append(h("option", { value: "" }, "Mọi trạng thái"));
    if (chiTiet.checked) statusSel.append(...RUN_STATUSES.map((s) => h("option", { value: s, title: runStatusTitle(s) }, runStatusText(s))));
    else statusSel.append(...NHOM_LOC_RUN.map((n) => h("option", { value: n.giaTri, title: n.title }, n.nhan)));
    statusSel.value = cu;
    if (statusSel.value !== cu) statusSel.value = "";
  }
  const stQ = query.get("status") ?? "";
  chiTiet.checked = !!stQ && !NHOM_LOC_RUN.some((n) => n.giaTri === stQ);
  veTrangThai();
  chiTiet.addEventListener("change", () => {
    // Đổi qua lại giữ ý lọc: nhóm → chi tiết thì bỏ chọn (nhiều mã); một mã chi tiết → nhóm chứa nó.
    const cu = statusSel.value;
    veTrangThai();
    if (!chiTiet.checked && cu && !statusSel.value) statusSel.value = NHOM_LOC_RUN.find((n) => (n.trangThai as string[]).includes(cu))?.giaTri ?? "";
  });
  const from = h("input", { type: "date", title: "Từ 0 giờ ngày này (giờ Việt Nam)", "data-testid": "f-from" });
  const to = h("input", { type: "date", title: "Đến hết ngày này (giờ Việt Nam)", "data-testid": "f-to" });
  botSel.value = query.get("botId") ?? "";
  statusSel.value = stQ;
  if (statusSel.value !== stQ) statusSel.value = "";
  from.value = query.get("from") ?? "";
  to.value = query.get("to") ?? "";
  const csv = h("a", { class: "btn", title: "Tải về tệp CSV (mở bằng Excel) gồm mọi lần chạy khớp bộ lọc hiện tại", "data-testid": "csv-link", download: "lich-su-run.csv" }, "Xuất Excel (CSV)");
  const applyBtn = h("button", { type: "button", class: "pri", title: "Tải lại bảng theo bộ lọc bên trên", "data-testid": "f-apply" }, "Lọc");
  const tbody = h("tbody", { "data-testid": "history-rows" });
  const more = h("button", { type: "button", hidden: true, title: "Tải thêm 50 lần chạy cũ hơn vào bảng", "data-testid": "load-more" }, "Tải thêm");
  const info = h("p", { class: "muted small", "data-testid": "history-info" });
  const errBox = h("div", { class: "err-box", hidden: true, "data-testid": "page-error" });
  // N11: lọc theo kịch bản (khớp đúng tên bundle). Tên quá dài → báo ngay, không gọi máy chủ.
  // (đặc tả đổi 12/10/2026) kịch bản đồ thị chạy theo ĐOẠN (mỗi đoạn một bundle): địa chỉ có thể LẶP ?bundleName=a&bundleName=b → lọc "một trong các tên" (liên kết ở thẻ ▶ Chạy của kịch bản sơ đồ).
  let bundleLoc: string[] = [...new Set(query.getAll("bundleName").filter(Boolean))];
  const BUNDLE_TOI_DA = 200;
  const chipKb = bundleLoc.length ? (() => {
    const bo = h("button", { type: "button", class: "mini ls-loc-bo", title: "Bỏ lọc theo kịch bản — hiện lần chạy của mọi kịch bản", "aria-label": "Bỏ lọc theo kịch bản", "data-testid": "ls-loc-kb-bo" }, "×");
    const hienTen = (b: string): HTMLElement => (b.length > BUNDLE_TOI_DA ? h("span", {}, `${b.slice(0, 40)}…`) : tenKb(b));
    const nhieu = bundleLoc.length > 1;
    const chip = h("div", { class: "row ls-loc-kb", style: "margin-bottom: 10px", "data-testid": "ls-loc-kb", dataset: { soBundle: String(bundleLoc.length) } }, h("span", { class: "small" }, "Đang lọc: "),
      nhieu ? h("span", { class: "small", title: "Lần chạy của mọi đoạn của một kịch bản (mỗi đoạn là một bản chạy riêng)" }, `kịch bản có ${bundleLoc.length} đoạn — `) : null,
      ...bundleLoc.slice(0, 3).flatMap((b, i) => [i ? ", " : "", hienTen(b)]), bundleLoc.length > 3 ? ` … và ${bundleLoc.length - 3} đoạn nữa` : "", " ", bo);
    bo.addEventListener("click", () => {
      bundleLoc = [];
      chip.remove();
      history.replaceState(null, "", `#/history${buildQuery({ botId: botSel.value, status: statusSel.value, from: from.value, to: to.value })}`);
      void loadPage(true);
    });
    return chip;
  })() : null;

  root.append(h("h1", {}, "Lịch sử chạy"),
    h("div", { class: "card" }, chipKb, h("div", { class: "field-row" },
      h("label", { class: "f" }, h("span", {}, "Bot"), botSel),
      h("div", { class: "f" }, h("label", { class: "f-nhan", for: "f-status-sel" }, "Trạng thái"), statusSel, h("label", { class: "inline small", title: "Hiện từng trạng thái riêng thay vì 5 nhóm" }, chiTiet, " Lọc chi tiết")),
      h("label", { class: "f" }, h("span", {}, "Từ ngày"), from),
      h("label", { class: "f" }, h("span", {}, "Đến ngày"), to)),
      h("div", { class: "row" }, applyBtn, csv)),
    info, errBox,
    h("div", { class: "tbl" }, h("table", { class: "bang-the ls-bang" }, h("thead", {}, h("tr", {}, h("th", {}, "Bắt đầu"), h("th", {}, "Bot"), h("th", {}, "Kịch bản"), h("th", { title: "Chạy thật, hoặc Chạy thử (cũ — trước khi bỏ Nháp)" }, "Chế độ"), h("th", {}, "Trạng thái"), h("th", { title: "Lý do lỗi/dừng do máy chủ hoặc bot báo" }, "Lý do"), h("th", {}, "Kết thúc"), h("th", {}))), tbody)),
    h("p", {}, more));

  let nextBefore: string | null = null;
  let count = 0;
  function filters(): Record<string, string | string[] | undefined> {
    // Ngày theo giờ Việt Nam: từ 00:00 ngày "từ" đến 24:00 ngày "đến" (+07:00).
    return {
      bundleName: bundleLoc.length ? bundleLoc : undefined,
      botId: botSel.value || undefined,
      status: statusSel.value || undefined,
      from: dauNgayVN(from.value),
      to: cuoiNgayVN(to.value),
    };
  }
  function refreshCsv(): void {
    csv.setAttribute("href", `/api/runs.csv${buildQuery(filters())}`);
  }
  function row(r: RunSummary): HTMLTableRowElement {
    // data-label: nhãn hiện kèm trong ô khi bảng thành thẻ ở màn hẹp (style.css .bang-the) — ở màn rộng không hiện, chữ trong ô giữ nguyên.
    return h("tr", { "data-testid": "history-row" },
      h("td", { class: "nowrap small ls-bd the-nua", "data-label": "Bắt đầu" }, fmtTime(r.startedAt ?? r.plannedAt)),
      h("td", { class: "name ls-bot", "data-label": "Bot" }, h("span", { title: r.botName }, r.botName)),
      h("td", { class: "name ls-kb", "data-label": "Kịch bản" }, tenKb(r.bundleName, { lienKet: true })),
      h("td", { class: "ls-cd the-nua", "data-label": "Chế độ" }, h("span", { class: modeTagClass(r.mode), title: modeTitle(r.mode) }, modeText(r.mode))),
      h("td", { class: "ls-tt the-nua", "data-label": "Trạng thái" }, h("span", { class: `tag ${runNhanHien(r).kind}`, title: runCoCanhBao(r) ? "Bot chạy xong nhưng không làm được việc chính — xem cột lý do" : runStatusTitle(r.status) }, runNhanHien(r).text)),
      ((td) => { ganLyDo(td, r.bundleName, r.reason); return td; })(h("td", { class: `reason small wrap ls-ld${runCoCanhBao(r) ? " warn" : ""}`, "data-label": "Lý do", "data-testid": "history-ly-do" })),
      h("td", { class: "nowrap small ls-kt the-nua", "data-label": "Kết thúc" }, fmtTime(r.endedAt)),
      h("td", { class: "ls-mo" }, r.runId ? h("a", { class: "btn mini", href: `#/runs/${encodeURIComponent(r.key)}`, title: "Mở chi tiết lần chạy: nhật ký từng bước" }, "Mở") : null));
  }
  async function loadPage(reset: boolean): Promise<void> {
    if (reset) { clear(tbody); nextBefore = null; count = 0; }
    errBox.hidden = true;
    const g = gen("history-load");
    const sai = bundleLoc.some((b) => b.length > BUNDLE_TOI_DA) ? `Tên kịch bản để lọc quá dài (tối đa ${BUNDLE_TOI_DA} ký tự)`
      : kiemKhoangNgay(from.value, to.value); // L-79: báo ngay bằng câu tiếng Việt, không gọi máy chủ
    if (sai) { clear(tbody); more.hidden = true; info.textContent = ""; errBox.textContent = sai; errBox.hidden = false; return; }
    if (reset) refreshCsv(); // SAU bộ kiểm ngày: filters() đổi ngày sang ISO (11/10: trước đây chạy trước → RangeError với năm 275760)
    try {
      const p = await api<Paged<RunSummary>>("GET", `/api/runs${buildQuery({ ...filters(), limit: 50, before: nextBefore })}`);
      if (!g() || !alive()) return;
      for (const r of p.items) tbody.append(row(r));
      count += p.items.length;
      nextBefore = p.nextBefore;
      more.hidden = !nextBefore;
      const coLoc = Object.values(filters()).some((v) => v !== undefined);
      info.textContent = count ? `${count} lần chạy${nextBefore ? " (còn nữa)" : ""}`
        : coLoc ? "Không có lần chạy nào khớp bộ lọc."
        : "Chưa có lần chạy nào. Vào Kịch bản, chọn kịch bản rồi bấm ▶ Chạy — mỗi lần bot chạy sẽ hiện ở đây.";
    } catch (e) {
      if (!g() || !alive()) return;
      errBox.textContent = `Không tải được lịch sử: ${errMsg(e)}`;
      errBox.hidden = false;
      info.textContent = "";
    }
  }
  applyBtn.addEventListener("click", () => {
    const q = buildQuery({ bundleName: bundleLoc, botId: botSel.value, status: statusSel.value, from: from.value, to: to.value });
    history.replaceState(null, "", `#/history${q}`);
    void busy(applyBtn, "Đang lọc…", () => loadPage(true));
  });
  more.addEventListener("click", () => void busy(more, "Đang tải…", () => loadPage(false)));
  await loadPage(true);
  return () => {};
}
